"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "system";

const ThemeContext = createContext<{ theme: Theme; setTheme: (theme: Theme) => void } | null>(null);

function resolvedTheme(theme: Theme): "light" | "dark" {
  if (theme === "system" && typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>("system");

  useEffect(() => {
    const root = document.documentElement;
    // Theme swaps are instant: hover/state color transitions must not animate
    // through intermediate (low-contrast) colors while the palette changes.
    root.classList.add("theme-switching");
    root.dataset.theme = resolvedTheme(theme);
    const frame = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => root.classList.remove("theme-switching")),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("ThemeProvider ausente");
  }
  return value;
}
